import { z } from 'zod';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';

export const smallSheetPropsSchema = z.union([
  z.object({
    visible: z.boolean().default(false),
    title: z.string(),
    accessibilityLabel: z.string().optional(),
    hasFooter: z.boolean().default(false),
    testID: z.string().optional(),
  }),
  z.object({
    visible: z.boolean().default(false),
    title: z.string().optional(),
    accessibilityLabel: z.string(),
    hasFooter: z.boolean().default(false),
    testID: z.string().optional(),
  }),
]);

export type SmallSheetProps = z.infer<typeof smallSheetPropsSchema>;

export function resolveAccessibleName(props: {
  title?: string;
  accessibilityLabel?: string;
}): string {
  if (props.accessibilityLabel) return props.accessibilityLabel;
  if (props.title) return props.title;
  return '';
}

export function didSmallSheetDismiss(previousVisible: boolean, visible: boolean): boolean {
  return previousVisible && !visible;
}

export const SHEET_TOP_RADIUS = radius.xxxl;
export const SHEET_HEADER_PADDING_HORIZONTAL = spacing.xxl;
export const SHEET_HEADER_PADDING_VERTICAL = spacing.sm;
export const SHEET_CONTENT_PADDING_HORIZONTAL = spacing.sm;
export const SHEET_CONTENT_PADDING_BOTTOM = spacing.xxl;
export const SHEET_CONTENT_PADDING_BOTTOM_WITH_FOOTER = 0;
export const SHEET_FOOTER_PADDING_HORIZONTAL = SHEET_CONTENT_PADDING_HORIZONTAL;
export const SHEET_FOOTER_PADDING_TOP = spacing.s;
export const SHEET_FOOTER_PADDING_BOTTOM = spacing.xs;
export const SHEET_OUTER_PADDING = spacing.s;

export const GRABBER_WIDTH = 36;
export const GRABBER_HEIGHT = 5;
export const GRABBER_RADIUS = radius.rounded;

// Clearance the sheet body keeps below the top safe-area edge. The body's
// height bound is `window height − top inset − this clearance` (native) /
// `viewport − this clearance` (web) — the same geometry as the OS sheet
// containers' tallest detent, so the bound tracks the space the OS actually
// gives the sheet instead of second-guessing it with a fixed ratio. The bound
// must exist at all: the fit-to-contents hosts measure the RN body's
// intrinsic (unbounded) content height, so without it the ScrollView never
// gets a constrained parent and cannot scroll (verified on-simulator — the
// OS clips the sheet but never propagates the constraint into RN layout).
// The grabber, header, and footer stay fixed; only the content scrolls.
export const SHEET_TOP_CLEARANCE = spacing.m;
